import { type PropsWithChildren, useEffect, useRef } from 'react';
import { analytics } from '@/analytics/client';
import { trackEvent } from '@/analytics/events';
import { hashSensitiveValue } from '@/analytics/privacy';
import { initPostHogAnalytics } from '@/analytics/posthog';
import { useAuth } from '@/context/useAuth';

function AuthAnalyticsSync() {
  const { isLoadingAuth, user } = useAuth();
  const previousUserId = useRef<string | null>(null);

  useEffect(() => {
    if (isLoadingAuth) {
      return;
    }

    if (user) {
      previousUserId.current = user.uid;

      void hashSensitiveValue(user.email ?? '').then((contactHash) => {
        analytics.identify(
          {
            userId: user.uid,
            contactHash,
          },
          {
            auth_provider: user.providerData[0]?.providerId ?? 'firebase',
            has_contact_email: Boolean(user.email),
          },
        );
      });

      trackEvent('auth_session_started', {
        auth_provider: user.providerData[0]?.providerId ?? 'firebase',
        has_contact_email: Boolean(user.email),
      });
      return;
    }

    if (previousUserId.current) {
      trackEvent('auth_session_ended');
      analytics.reset();
      previousUserId.current = null;
    }
  }, [isLoadingAuth, user]);

  return null;
}

export function PostHogAnalyticsProvider({ children }: PropsWithChildren) {
  useEffect(() => {
    initPostHogAnalytics();
  }, []);

  return (
    <>
      <AuthAnalyticsSync />
      {children}
    </>
  );
}
